/**
 * Auth client actions & auth state listener.
 */

import {
	signInWithEmailAndPassword,
	signInWithPopup,
	GoogleAuthProvider,
	signOut as firebaseSignOut,
	onAuthStateChanged,
	type User
} from 'firebase/auth';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, deleteField } from 'firebase/firestore';
import { auth, storage, db } from './config.ts';
import { authStore } from '$lib/stores/auth.svelte';
import { updateUserProfile, subscribeToUserProfile, createAccountViaApi } from './users.ts';
import { resizeAvatar } from '$lib/utils/image';

let unsubscribeProfile: (() => void) | null = null;
let initialized = false;

export function initAuth(): void {
	if (typeof window === 'undefined' || initialized) return;
	initialized = true;

	onAuthStateChanged(
		auth(),
		(user: User | null) => {
			if (unsubscribeProfile) {
				unsubscribeProfile();
				unsubscribeProfile = null;
			}

			if (user) {
				// Only set initial values on profile creation, never overwrite existing
				updateUserProfile(user.uid, {
					...(user.displayName ? { displayName: user.displayName } : {}),
					...(user.email ? { email: user.email } : {})
				}).catch((err) => console.error('[Auth] Error initializing user profile doc:', err));

				// Listen to user profile doc
				unsubscribeProfile = subscribeToUserProfile(user.uid, (profile) => {
					authStore.setAuth(user, profile);
				});
			} else {
				authStore.setAuth(null, null);
			}
		},
		(error) => {
			console.error('[Auth] Error in onAuthStateChanged:', error);
			authStore.setAuth(null, null);
		}
	);
}

// Auto-initialize in browser if module is imported
if (typeof window !== 'undefined') {
	initAuth();
}

export async function loginWithEmail(email: string, pass: string): Promise<void> {
	const cred = await signInWithEmailAndPassword(auth(), email, pass);
	authStore.setAuth(cred.user, null);
}

export async function registerWithEmail(
	email: string,
	pass: string,
	displayName: string,
	handle: string
): Promise<void> {
	await createAccountViaApi(email, pass, displayName, handle);
	const cred = await signInWithEmailAndPassword(auth(), email, pass);
	authStore.setAuth(cred.user, null);
}

export async function loginWithGoogle(): Promise<void> {
	const provider = new GoogleAuthProvider();
	const cred = await signInWithPopup(auth(), provider);
	authStore.setAuth(cred.user, null);
}

export async function logout(): Promise<void> {
	await firebaseSignOut(auth());
}

export async function uploadProfilePhoto(file: File): Promise<string> {
	const currentUser = auth().currentUser;
	if (!currentUser) throw new Error('User must be logged in to upload a photo');

	const resized = await resizeAvatar(file, 256);
	const storageRef = ref(storage(), `profiles/${currentUser.uid}/avatar`);
	await uploadBytes(storageRef, resized);
	const downloadURL = await getDownloadURL(storageRef);

	await updateUserProfile(currentUser.uid, { photoURL: downloadURL });
	return downloadURL;
}

export async function removeProfilePhoto(): Promise<void> {
	const currentUser = auth().currentUser;
	if (!currentUser) throw new Error('User must be logged in to remove a photo');

	const storageRef = ref(storage(), `profiles/${currentUser.uid}/avatar`);
	try {
		await deleteObject(storageRef);
	} catch {
		// File may not exist — that's fine
	}

	const userRef = doc(db(), 'users', currentUser.uid);
	await updateDoc(userRef, { photoURL: deleteField() });
}
